import React, { useState, useEffect } from 'react';
import { Dialog, IconButton, DialogContent, DialogActions, DialogTitle, Stepper, Step, StepLabel, Box } from '@mui/material';
import { GridRowId } from '@mui/x-data-grid';
import { Button } from 'components/index';
import SelectedProjectChips from '../project/SelectedProjectChips';
import ProjectsList from 'pages/ProjectsList';
import CloseIcon from '@mui/icons-material/Close';
import { Division } from '../glossary/glossary.type';
import { getProjectInfo } from '../project/project.api';
import { Collapse } from '@mui/material';
import DivisionsList from './DivisionsList';
import { Typography } from 'components/index';
import { CircularProgress } from '@mui/material';

interface SelectedProject {
  id: string;
  title: string;
  number: string;
}

interface AddCompsWithDivisionsDialogProps {
  open: boolean;
  onClose: () => void;
  divisions: Division[];
  onSelectCompsAndDivisions: (selectedCompIds: string[], selectedDivisionIds: string[]) => void | Promise<void>;
  isApplying?: boolean;
  disabledCompsIds?: string[];
  isAdding?: boolean;
}

const AddCompsWithDivisionsDialog: React.FC<AddCompsWithDivisionsDialogProps> = ({
  open,
  onClose,
  divisions,
  onSelectCompsAndDivisions,
  isApplying,
  disabledCompsIds = [],
  isAdding = false,
}) => {
  const [selectedCompIds, setSelectedCompIds] = useState<string[]>([]);
  const [selectedProjectDetails, setSelectedProjectDetails] = useState<SelectedProject[]>([]);
  const [selectedDivisionIds, setSelectedDivisionIds] = useState<string[]>([]);
  const [currentStep, setCurrentStep] = useState(0);
  const [loadingProjectDetails, setLoadingProjectDetails] = useState(false);
  console.log('selectedProjectDetails', selectedProjectDetails);

  useEffect(() => {
    if (open) {
      setSelectedCompIds([]);
      setSelectedProjectDetails([]);
      setSelectedDivisionIds([]);
      setCurrentStep(0);
    }
  }, [open]);
  // Fetch project details when selectedCompIds changes
  useEffect(() => {
    const fetchProjectDetails = async () => {
      if (selectedCompIds.length === 0) {
        setSelectedProjectDetails([]);
        return;
      }

      setLoadingProjectDetails(true);
      try {
        const projectDetailsPromises = selectedCompIds.map(async projectId => {
          try {
            const projectInfo = await getProjectInfo(projectId);
            return {
              id: projectId,
              title: projectInfo.projectTitle || 'Unknown Title',
              number: projectInfo.projectNumber || 'Unknown Number',
            };
          } catch (error) {
            console.error(`Failed to fetch project ${projectId}:`, error);
            return {
              id: projectId,
              title: 'Unknown Title',
              number: 'Unknown Number',
            };
          }
        });

        const projectDetails = await Promise.all(projectDetailsPromises);
        setSelectedProjectDetails(projectDetails);
      } catch (error) {
        console.error('Failed to fetch project details:', error);
        setSelectedProjectDetails([]);
      } finally {
        setLoadingProjectDetails(false);
      }
    };

    fetchProjectDetails();
  }, [selectedCompIds]);

  const handleNext = React.useCallback(() => {
    if (currentStep === 0 && selectedCompIds.length > 0) {
      setCurrentStep(1);
    }
  }, [currentStep, selectedCompIds]);

  const handleBack = React.useCallback(() => {
    if (currentStep === 1) {
      setCurrentStep(0);
    }
  }, [currentStep]);

  const handleSelectDivisions = React.useCallback(async () => {
    if (onSelectCompsAndDivisions) {
      await onSelectCompsAndDivisions(selectedCompIds, selectedDivisionIds);
    }
    onClose();
    setSelectedCompIds([]);
    setSelectedProjectDetails([]);
    setSelectedDivisionIds([]);
    setCurrentStep(0);
  }, [onSelectCompsAndDivisions, selectedCompIds, selectedDivisionIds, onClose]);

  const handleProjectsSelect = React.useCallback((selected: readonly GridRowId[], filterString?: string) => {
    setSelectedCompIds(selected.map(String));
  }, []);

  const handleDivisionSelectionChange = React.useCallback((ids: string[]) => {
    setSelectedDivisionIds(ids);
  }, []);

  const resolvedProjectDetails = React.useMemo(() => {
    return Object.fromEntries(selectedProjectDetails.map(p => [p.id, { number: p.number, title: p.title }]));
  }, [selectedProjectDetails]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      sx={{
        '& .MuiDialog-paper': {
          maxWidth: '1500px',
          height: '900px',
        },
      }}
      fullWidth
      maxWidth='xl'
    >
      <DialogTitle sx={{ paddingBottom: 0, display: 'flex', alignItems: 'baseline', gap: 1 }}>
        {currentStep === 0 ? 'Select Comparables' : 'Select Divisions'}
        {currentStep === 0 && selectedCompIds.length > 0 && (
          <Typography variant='subtitle2' color='text.secondary'>
            ({selectedCompIds.length} selected)
          </Typography>
        )}
      </DialogTitle>
      <IconButton onClick={onClose} style={{ position: 'absolute', right: 8, top: 8 }}>
        <CloseIcon />
      </IconButton>
      <Box sx={{ px: 3, pt: 2 }}>
        <Stepper activeStep={currentStep} alternativeLabel>
          <Step key='comps'>
            <StepLabel>Select Comparables</StepLabel>
          </Step>
          <Step key='divisions'>
            <StepLabel>Select Divisions</StepLabel>
          </Step>
        </Stepper>
      </Box>
      <DialogContent sx={{ paddingTop: 0 }}>
        {currentStep === 0 ? (
          <ProjectsList
            onProjectsSelect={handleProjectsSelect}
            isFromReport={true}
            disabledProjectsIds={disabledCompsIds}
            shouldRenderMinimalActions={true}
            enableSingleSelection={false}
            initialSelectedProjects={selectedCompIds}
            resolvedProjectDetails={resolvedProjectDetails}
          />
        ) : (
          <Box sx={{ p: 2 }}>
            <Collapse in={selectedCompIds.length > 0} timeout={300} easing='cubic-bezier(0.25, 0.46, 0.45, 0.94)'>
              <Box sx={{ mb: 3 }}>
                <Typography variant='subtitle2' color='text.primary' gutterBottom>
                  Selected Projects ({selectedCompIds.length}):
                </Typography>
                {loadingProjectDetails ? (
                  <CircularProgress size={20} />
                ) : (
                  <SelectedProjectChips
                    selectedIds={selectedCompIds}
                    resolvedDetails={resolvedProjectDetails}
                    showArrows
                    allowDelete={false}
                  />
                )}
              </Box>
            </Collapse>
            <DivisionsList
              divisions={divisions}
              selectedDivisionIds={selectedDivisionIds}
              onSelectionChange={handleDivisionSelectionChange}
              height={580}
            />
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        {currentStep === 1 && (
          <Button variant='outlined' onClick={handleBack}>
            Back
          </Button>
        )}
        {currentStep === 0 ? (
          <Button variant='contained' onClick={handleNext} color='primary' disabled={selectedCompIds.length === 0}>
            Next
          </Button>
        ) : (
          <Button
            variant='contained'
            onClick={handleSelectDivisions}
            color='primary'
            loading={isAdding}
            disabled={selectedDivisionIds.length === 0}
          >
            Select Divisions
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default AddCompsWithDivisionsDialog;
